import { Box, Image, Text, VStack, chakra } from '@chakra-ui/react';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import type { MentionCandidate } from '../mentions/applyMention';

type Props = {
  suggestions: MentionCandidate[];
  activeIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (candidate: MentionCandidate) => void;
};

/**
 * The `@` picker.
 *
 * Anchored ABOVE the composer: the composer is docked to the bottom of the viewport under
 * env(safe-area-inset-bottom), so a dropdown would open off-screen.
 *
 * Every row suppresses mousedown. A plain click blurs the input first, and on blur
 * selectionStart is unreliable -- and on a phone the soft keyboard closes, which makes
 * picking a name feel like the composer threw the message away.
 */
export function MentionSuggestions({ suggestions, activeIndex, onHighlight, onSelect }: Props) {
  if (suggestions.length === 0) return null;

  return (
    <Box
      position="absolute"
      bottom="calc(100% + 8px)"
      left={0}
      right={0}
      zIndex={20}
      bg="white"
      borderWidth={1}
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      maxH="240px"
      overflowY="auto"
      role="listbox"
      aria-label="Tag someone"
    >
      <VStack align="stretch" gap={0}>
        {suggestions.map((candidate, index) => (
          <chakra.button
            key={candidate.id}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            display="flex"
            alignItems="center"
            gap={3}
            px={3}
            py={2}
            textAlign="left"
            bg={index === activeIndex ? 'blue.50' : 'transparent'}
            _hover={{ bg: 'blue.50' }}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHighlight(index)}
            onClick={() => onSelect(candidate)}
          >
            <Image
              src={candidate.image || DEFAULT_PROFILE_IMAGE}
              alt=""
              boxSize="28px"
              borderRadius="full"
              objectFit="cover"
              flexShrink={0}
            />
            <Text as="span" fontSize="sm" color="gray.800" lineClamp={1}>
              {candidate.name}
            </Text>
          </chakra.button>
        ))}
      </VStack>
    </Box>
  );
}
